import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    Button,
    Typography,
    Chip,
    Checkbox,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Box,
    DialogTitle,
    DialogContent,
    Avatar,
    IconButton,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import ConfirmationDialog from './ConfirmationDialog.comp';

const ORANGE = '#F15A24';

const normCode = (c) => (c == null ? '' : String(c));

const pickerCheckboxSx = {
    p: 0.75,
    color: ORANGE,
    '&.Mui-checked': {
        color: '#fff',
        bgcolor: ORANGE,
        borderRadius: '6px',
        '&:hover': { bgcolor: '#e04d18' },
    },
    '& .MuiSvgIcon-root': { fontSize: 28 },
};

/** Standard assessment picker — Manage Classes (class-level linking).  */
const ManageClassesLinkAssessmentDialog = ({
    assessments,
    open,
    onSubmit,
    loading,
    onClose,
    orgName,
    orgCode,
    linkedAssessmentCodes = [],
    orgWideLinkedCodes = [],
    sectionLinkedCodes = [],
    linkingLoading = false,
    forSectionName,
    forSectionCode,
}) => {
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
    const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedCodes([]);
            setShowFirstConfirmation(false);
            setShowSecondConfirmation(false);
        }
    }, [open]);

    const linkedSet = new Set((linkedAssessmentCodes || []).map(normCode));
    const orgWideSet = new Set((orgWideLinkedCodes || []).map(normCode));
    const sectionLinkedSet = new Set((sectionLinkedCodes || []).map(normCode));

    const handleToggle = (code) => {
        const key = normCode(code);
        setSelectedCodes((prev) => {
            const prevNorm = prev.map(normCode);
            return prevNorm.includes(key) ? prev.filter((c) => normCode(c) !== key) : [...prev, code];
        });
    };

    const handleSubmit = () => setShowFirstConfirmation(true);
    const handleFirstConfirm = () => {
        setShowFirstConfirmation(false);
        setShowSecondConfirmation(true);
    };
    const handleSecondConfirm = () => {
        setShowSecondConfirmation(false);
        onSubmit(orgCode, selectedCodes);
    };

    const linked = assessments.filter((a) => linkedSet.has(normCode(a.code)));
    const unlinked = assessments.filter((a) => !linkedSet.has(normCode(a.code)));
    const isClassContext = Boolean(forSectionName || forSectionCode);
    const pickerHint = isClassContext
        ? 'Select the assessment(s) that you would like to link to this class.'
        : 'Select the assessment(s) that you would like to link to this client.';
    // Class name only — the class code is not shown (Ian-PW,SNW,SW-19Oct-1).
    const classLabel = forSectionName || forSectionCode || '';
    const linkTargetLabel = isClassContext ? (classLabel || 'this class') : orgName;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    position: 'relative',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    maxHeight: 'min(92vh, 880px)',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'var(--brand-surface, #fff)',
                    backgroundImage: 'none',
                },
            }}
            BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } }}
        >
            <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
                <IconButton aria-label="Close dialog" onClick={onClose} size="small" disabled={loading}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogTitle sx={{ color: 'var(--brand-text, #111)', fontSize: '22px', fontWeight: 800, textAlign: 'center', mb: 1, pt: 4, pb: 1, pr: 5, flexShrink: 0 }}>
                Link Standard Assessments
                {isClassContext && classLabel ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Chip
                            label={classLabel}
                            size="small"
                            sx={{ backgroundColor: 'rgba(241, 90, 36, 0.1)', color: ORANGE, fontWeight: 700, fontSize: '12px', height: 24 }}
                        />
                    </Box>
                ) : (
                    <Typography component="div" sx={{ fontSize: '15px', fontWeight: 500, color: 'var(--brand-text-muted, #666)', mt: 1 }}>
                        {orgName}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 4 }, py: 2, flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                {linkingLoading ? (
                    <Typography>Loading assessments...</Typography>
                ) : (
                    <>
                        <Typography sx={{ textAlign: 'center', fontSize: '16px', color: 'var(--brand-text-muted, #666)', mb: 2 }}>{pickerHint}</Typography>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 'none',
                                border: '1px solid var(--brand-border, #eee)',
                                borderRadius: '16px',
                                maxHeight: { xs: '45vh', sm: '52vh' },
                                overflow: 'auto',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, color: 'var(--brand-text, #444)', borderBottom: '2px solid var(--brand-border, #eee)', backgroundColor: 'var(--brand-bg, #f8f9fa)' }}>
                                            Assessment name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: 'var(--brand-text, #444)', borderBottom: '2px solid var(--brand-border, #eee)', backgroundColor: 'var(--brand-bg, #f8f9fa)' }}>
                                            Time (mins)
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 800,
                                                color: 'var(--brand-text, #444)',
                                                borderBottom: '2px solid var(--brand-border, #eee)',
                                                backgroundColor: 'var(--brand-bg, #f8f9fa)',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Select
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...linked, ...unlinked].map((row) => {
                                        const rowCode = normCode(row.code);
                                        const alreadyLinked = linkedSet.has(rowCode);
                                        const isOrgWide = orgWideSet.has(rowCode);
                                        const isSectionLinked = sectionLinkedSet.has(rowCode) && !isOrgWide;
                                        const notReady = !row.is_ready;
                                        const linkedHint = isOrgWide
                                            ? 'Already linked org-wide — all classes already have access. No section link needed.'
                                            : 'Already linked to this class';
                                        return (
                                            <TableRow
                                                key={row.code || row.id}
                                                sx={{
                                                    backgroundColor: alreadyLinked ? 'rgba(241,90,36,0.06)' : notReady ? 'var(--brand-bg, #fafafa)' : 'inherit',
                                                }}
                                            >
                                                <TableCell sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--brand-border, #eee)' }}>
                                                    <Avatar
                                                        src={row.image_url}
                                                        variant="rounded"
                                                        sx={{ width: 56, height: 56, border: '1px solid var(--brand-border, #eee)', borderRadius: '12px' }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, ml: '15px' }}>
                                                        <Typography component="span" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                                                            {row.full_name}
                                                        </Typography>
                                                        {isOrgWide ? (
                                                            <Chip label="Org-wide" size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(0, 151, 178, 0.12)', color: '#007a8c' }} />
                                                        ) : null}
                                                        {isSectionLinked ? (
                                                            <Chip label="Linked to class" size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(241, 90, 36, 0.12)', color: ORANGE }} />
                                                        ) : null}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid var(--brand-border, #eee)', color: 'var(--brand-text-muted, #555)' }}>{row.time_to_complete_minutes}</TableCell>
                                                <TableCell align="center" sx={{ borderBottom: '1px solid var(--brand-border, #eee)' }}>
                                                    {alreadyLinked ? (
                                                        <Chip
                                                            label="Already Linked"
                                                            size="small"
                                                            title={linkedHint}
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                                color: '#0097a7',
                                                                fontWeight: 'bold',
                                                            }}
                                                        />
                                                    ) : notReady ? (
                                                        <Chip
                                                            label="Not Ready"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                                color: ORANGE,
                                                                fontWeight: 'bold',
                                                                border: '1px solid rgba(241, 90, 36, 0.45)',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Checkbox
                                                            checked={selectedCodes.some((c) => normCode(c) === rowCode)}
                                                            onChange={() => handleToggle(row.code)}
                                                            sx={pickerCheckboxSx}
                                                            disabled={alreadyLinked || notReady}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    flexShrink: 0,
                    pt: 2.5,
                    pb: 2,
                    px: 2,
                    borderTop: '1px solid var(--brand-border, #eee)',
                    bgcolor: 'var(--brand-surface, #fff)',
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            minWidth: 180,
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            backgroundColor: 'var(--brand-surface, #fff)',
                            color: 'var(--brand-text, #111)',
                            fontWeight: 700,
                            fontSize: '16px',
                            textTransform: 'none',
                            border: '1.5px solid var(--brand-border, #111)',
                            '&:hover': { backgroundColor: 'var(--brand-bg, #f7f7f7)', borderColor: 'var(--brand-border, #111)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || selectedCodes.length === 0}
                        sx={{
                            minWidth: 180,
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            backgroundColor: '#FF6900',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '16px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: '#FFD0AA', color: '#fff' },
                        }}
                    >
                        {loading ? 'Linking...' : 'Submit'}
                    </Button>
                </Box>
            </DialogActions>
            <ConfirmationDialog
                open={showFirstConfirmation}
                onClose={() => setShowFirstConfirmation(false)}
                onConfirm={handleFirstConfirm}
                title="Confirm linking"
                message={`Do you want to link <b>${selectedCodes.length}</b> assessment(s) to <b>${linkTargetLabel}</b>?`}
                confirmText="Proceed"
                cancelText="Cancel"
            />
            <ConfirmationDialog
                open={showSecondConfirmation}
                onClose={() => setShowSecondConfirmation(false)}
                onConfirm={handleSecondConfirm}
                title="Are You Sure?"
                message="Are you sure? Unlinking will not be possible once linked."
                confirmText="Yes, Link Now"
                cancelText="Cancel"
                isWarning={true}
            />
        </Dialog>
    );
};

export default ManageClassesLinkAssessmentDialog;
